import type { Contact, SeniorityLevel, BuyingCommitteeRole, UseCaseItem, OutboundPriority, PersonaImpactRow, PersonaValueStatement, AccountDepartment } from '../types';

const SENIORITY_PATTERNS: { pattern: RegExp; seniority: SeniorityLevel; level: number }[] = [
  { pattern: /\b(chief|ceo|cfo|cio|cto|ciso|coo|cpo|cmo|president)\b/i, seniority: 'executive', level: 1 },
  { pattern: /\b(svp|evp|senior\s+vice\s+president|executive\s+vice\s+president)\b/i, seniority: 'executive_leadership', level: 2 },
  { pattern: /\b(vp|vice\s+president)\b/i, seniority: 'senior_leadership', level: 2 },
  { pattern: /\b(head\s+of|director|sr\.?\s+director|senior\s+director)\b/i, seniority: 'department_leader', level: 3 },
  { pattern: /\b(manager|lead|team\s+lead|principal)\b/i, seniority: 'team_manager', level: 4 },
  { pattern: /\b(engineer|analyst|specialist|developer|architect|consultant|associate|coordinator|staff)\b/i, seniority: 'individual_contributor', level: 5 },
];

const RAW_SENIORITY_MAP: Record<string, SeniorityLevel> = {
  'c-suite': 'executive',
  'cxo': 'executive',
  'executive': 'executive',
  'founder': 'executive',
  'partner': 'executive',
  'owner': 'executive',
  'svp': 'executive_leadership',
  'evp': 'executive_leadership',
  'vp': 'senior_leadership',
  'vice president': 'senior_leadership',
  'director': 'department_leader',
  'head': 'department_leader',
  'senior director': 'department_leader',
  'sr. director': 'department_leader',
  'manager': 'team_manager',
  'lead': 'team_manager',
  'principal': 'team_manager',
  'senior': 'individual_contributor',
  'entry': 'individual_contributor',
  'junior': 'individual_contributor',
  'intern': 'individual_contributor',
  'associate': 'individual_contributor',
  'individual contributor': 'individual_contributor',
  'ic': 'individual_contributor',
  'staff': 'individual_contributor',
  'training': 'individual_contributor',
  'unpaid': 'individual_contributor',
};

const SENIORITY_LEVEL_MAP: Record<SeniorityLevel, number> = {
  'executive': 1,
  'executive_leadership': 2,
  'senior_leadership': 2,
  'department_leader': 3,
  'team_manager': 4,
  'individual_contributor': 5,
  '': 5,
};

export function normaliseSeniority(raw: string, title: string): { seniority: SeniorityLevel; level: number } {
  if (!raw) return inferSeniority(title);

  const normalised = raw.toLowerCase().trim();

  const validSystemValues: SeniorityLevel[] = [
    'executive', 'executive_leadership', 'senior_leadership',
    'department_leader', 'team_manager', 'individual_contributor',
  ];
  if (validSystemValues.includes(normalised as SeniorityLevel)) {
    return { seniority: normalised as SeniorityLevel, level: SENIORITY_LEVEL_MAP[normalised as SeniorityLevel] };
  }

  const mapped = RAW_SENIORITY_MAP[normalised];
  if (mapped) {
    return { seniority: mapped, level: SENIORITY_LEVEL_MAP[mapped] };
  }

  return inferSeniority(title);
}

const DEPARTMENT_PATTERNS: { pattern: RegExp; department: string }[] = [
  { pattern: /\b(cyber\s+defence|cyber\s+defense)\b/i, department: 'Cyber Defence' },
  { pattern: /\b(cyber\s+assurance|cyber\s+governance)\b/i, department: 'Cyber Assurance' },
  { pattern: /\b(security\s+architecture|security\s+design)\b/i, department: 'Security Architecture' },
  { pattern: /\b(security|ciso|infosec|cyber)\b/i, department: 'Security' },
  { pattern: /\b(data\s+engineering)\b/i, department: 'Data Engineering' },
  { pattern: /\b(data\s+scien)/i, department: 'Data Science' },
  { pattern: /\b(sre|site\s+reliability)\b/i, department: 'SRE' },
  { pattern: /\b(devops)\b/i, department: 'DevOps' },
  { pattern: /\b(platform)\b/i, department: 'Platform Engineering' },
  { pattern: /\b(engineering|software|developer)\b/i, department: 'Engineering' },
  { pattern: /\b(infrastructure|cloud|network|systems)\b/i, department: 'Infrastructure' },
  { pattern: /\b(data|analytics|bi|machine\s+learning|ai)\b/i, department: 'Data & Analytics' },
  { pattern: /\b(product|ux|design)\b/i, department: 'Product' },
  { pattern: /\b(it|information\s+technology|cio)\b/i, department: 'IT' },
  { pattern: /\b(risk|compliance|governance|audit)\b/i, department: 'Risk & Compliance' },
  { pattern: /\b(finance|cfo|accounting|treasury)\b/i, department: 'Finance' },
  { pattern: /\b(sales|revenue|business\s+development)\b/i, department: 'Sales' },
  { pattern: /\b(marketing|cmo|brand|growth)\b/i, department: 'Marketing' },
  { pattern: /\b(operations|coo|supply\s+chain|logistics)\b/i, department: 'Operations' },
  { pattern: /\b(hr|human\s+resources|people|talent)\b/i, department: 'Human Resources' },
  { pattern: /\b(legal|general\s+counsel)\b/i, department: 'Legal' },
];

const BUYING_ROLE_PATTERNS: { pattern: RegExp; role: BuyingCommitteeRole }[] = [
  { pattern: /\b(chief|ceo|cfo|cio|cto|ciso|svp|evp|president)\b/i, role: 'economic_buyer' },
  { pattern: /\b(architect|engineer|technical|developer|platform|staff)\b/i, role: 'technical_buyer' },
  { pattern: /\b(vp|head\s+of|director)\b/i, role: 'champion' },
  { pattern: /\b(manager|lead|principal)\b/i, role: 'influencer' },
  { pattern: /\b(analyst|specialist|consultant|coordinator)\b/i, role: 'validator' },
];

export function inferSeniority(title: string): { seniority: SeniorityLevel; level: number } {
  for (const { pattern, seniority, level } of SENIORITY_PATTERNS) {
    if (pattern.test(title)) return { seniority, level };
  }
  return { seniority: 'individual_contributor', level: 5 };
}

export function inferDepartment(title: string): string {
  for (const { pattern, department } of DEPARTMENT_PATTERNS) {
    if (pattern.test(title)) return department;
  }
  return 'General';
}

export function inferBuyingRole(title: string): BuyingCommitteeRole {
  for (const { pattern, role } of BUYING_ROLE_PATTERNS) {
    if (pattern.test(title)) return role;
  }
  return '';
}

export function inferOrgPlacementConfidence(contact: Contact): number {
  let score = 30;
  if (contact.role) score += 25;
  if (contact.department) score += 15;
  if (contact.email) score += 10;
  if (contact.linkedin_url) score += 10;
  if (contact.seniority) score += 10;
  return Math.min(score, 100);
}

export interface OrgNode {
  contact: Contact;
  children: OrgNode[];
}

export function buildOrgTree(contacts: Contact[]): OrgNode[] {
  const sorted = [...contacts].sort((a, b) => a.org_level - b.org_level);
  const nodeMap = new Map<string, OrgNode>();
  const roots: OrgNode[] = [];

  for (const contact of sorted) {
    nodeMap.set(contact.id, { contact, children: [] });
  }

  for (const contact of sorted) {
    const node = nodeMap.get(contact.id)!;
    if (contact.reports_to_id && nodeMap.has(contact.reports_to_id)) {
      nodeMap.get(contact.reports_to_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
}

const PARENT_DOMAIN_MAP: Record<string, string> = {
  'Cyber Defence': 'Security',
  'Cyber Assurance': 'Security',
  'Security Architecture': 'Security',
  'Data Engineering': 'Engineering',
  'Data Science': 'Data & Analytics',
  'SRE': 'Engineering',
  'DevOps': 'Engineering',
  'Platform Engineering': 'Engineering',
};

function getDomainFamily(dept: string): string {
  return PARENT_DOMAIN_MAP[dept] || dept;
}

export function autoAssignHierarchy(contacts: Contact[]): Contact[] {
  const normalised = contacts.map(c => {
    const { seniority, level } = normaliseSeniority(c.seniority, c.role);
    const department = c.department || inferDepartment(c.role);
    const buyingRole = c.buying_committee_role || inferBuyingRole(c.role);
    return { ...c, seniority, org_level: level, department, buying_committee_role: buyingRole };
  });

  const sorted = [...normalised].sort((a, b) => a.org_level - b.org_level);

  const deptLeaders = new Map<string, Contact>();
  const familyLeaders = new Map<string, Contact>();

  for (const c of sorted) {
    const dept = c.department || 'General';
    if (!deptLeaders.has(dept) || c.org_level < deptLeaders.get(dept)!.org_level) {
      deptLeaders.set(dept, c);
    }
    const family = getDomainFamily(dept);
    if (!familyLeaders.has(family) || c.org_level < familyLeaders.get(family)!.org_level) {
      familyLeaders.set(family, c);
    }
  }

  const cSuite = sorted.filter(c => c.org_level === 1);
  const topLeader = cSuite.length > 0 ? cSuite[0] : sorted[0];

  const result: Contact[] = [];
  for (const contact of sorted) {
    let reportsTo: string | null = null;
    if (contact.id !== topLeader?.id) {
      const dept = contact.department || 'General';
      const family = getDomainFamily(dept);
      const deptLead = deptLeaders.get(dept);
      const familyLead = familyLeaders.get(family);

      if (deptLead && deptLead.id !== contact.id && deptLead.org_level < contact.org_level) {
        reportsTo = deptLead.id;
      } else if (familyLead && familyLead.id !== contact.id && familyLead.org_level < contact.org_level) {
        reportsTo = familyLead.id;
      } else if (topLeader) {
        reportsTo = topLeader.id;
      }
    }
    result.push({
      ...contact,
      reports_to_id: reportsTo,
      org_placement_confidence: inferOrgPlacementConfidence(contact),
    });
  }

  return result;
}

const TITLE_SYNONYMS: Record<string, string[]> = {
  'cio': ['chief information officer', 'cio'],
  'cto': ['chief technology officer', 'cto'],
  'ciso': ['chief information security officer', 'ciso'],
  'cfo': ['chief financial officer', 'cfo'],
  'coo': ['chief operating officer', 'coo'],
  'ceo': ['chief executive officer', 'ceo'],
  'cro': ['chief revenue officer', 'cro'],
  'cpo': ['chief product officer', 'cpo'],
  'vp': ['vice president', 'vp', 'vice-president'],
  'svp': ['senior vice president', 'svp'],
  'evp': ['executive vice president', 'evp'],
  'director': ['director', 'sr. director', 'senior director', 'sr director'],
  'head': ['head of', 'head,'],
  'manager': ['manager', 'mgr'],
  'lead': ['lead', 'team lead', 'tech lead'],
  'principal': ['principal'],
  'staff': ['staff'],
  'architect': ['architect', 'solutions architect', 'enterprise architect'],
  'engineer': ['engineer', 'developer', 'sre'],
  'analyst': ['analyst', 'business analyst'],
};

const GENERIC_TITLE_WORDS = new Set(['vp', 'svp', 'evp', 'vice', 'president', 'director', 'head', 'manager', 'mgr',
  'lead', 'principal', 'staff', 'chief', 'officer', 'senior', 'sr', 'jr', 'junior', 'associate', 'assistant', 'executive',
  'global', 'group', 'regional', 'team', 'tech', 'engineer', 'developer', 'architect', 'analyst', 'specialist', 'consultant',
  'coordinator', 'administrator', 'member', 'general', 'deputy', 'interim', 'acting', 'cio', 'cto', 'ciso', 'cfo', 'coo',
  'ceo', 'cro', 'cpo', 'emea', 'apac', 'americas', 'international', 'national', 'corporate', 'business', 'enterprise',
  'solutions', 'services', 'service', 'strategy', 'strategic', 'transformation', 'technology', 'information', 'systems',
  'management', 'office', 'partner', 'owner']);

function normaliseTitleForMatching(title: string): string {
  let t = title.toLowerCase().trim();
  for (const [canonical, synonyms] of Object.entries(TITLE_SYNONYMS)) {
    for (const syn of synonyms) {
      if (t.includes(syn)) {
        t = t + ' ' + canonical;
      }
    }
  }
  return t;
}

export function computePersonaMatch(
  contactTitle: string,
  personaTargets: string[]
): { match: string; score: number } {
  if (!personaTargets.length || !contactTitle) return { match: '', score: 0 };

  const titleNorm = normaliseTitleForMatching(contactTitle);
  let bestMatch = '';
  let bestScore = 0;

  for (const persona of personaTargets) {
    const personaNorm = normaliseTitleForMatching(persona);

    if (titleNorm.includes(personaNorm) || personaNorm.includes(titleNorm)) {
      if (100 > bestScore) {
        bestScore = 100;
        bestMatch = persona;
      }
      continue;
    }

    const stopWords = new Set(['of', 'or', 'and', 'the', 'for', 'in', 'a', 'an', 'at', 'to', 'uk', '&']);
    const personaWords = personaNorm.split(/[\s,]+/).filter(w => w.length > 1 && !stopWords.has(w));
    const titleWords = titleNorm.split(/[\s,]+/).filter(w => w.length > 1 && !stopWords.has(w));

    let matched = 0;
    let domainMatched = false;
    const domainWords = new Set(['security', 'cyber', 'engineering', 'data', 'platform', 'infrastructure',
      'cloud', 'sre', 'devops', 'operations', 'it', 'risk', 'compliance', 'defence', 'defense',
      'architecture', 'network', 'pricing', 'digital', 'analytics', 'assurance']);

    for (const word of personaWords) {
      if (titleWords.some(tw => tw === word || tw.includes(word) || word.includes(tw))) {
        matched++;
        // Any shared functional word counts (e.g. sales, enablement), not only technical ones;
        // seniority and generic job-family words alone still cannot lift the cap.
        if (domainWords.has(word) || (word.length > 2 && !GENERIC_TITLE_WORDS.has(word))) domainMatched = true;
      }
    }

    let score = personaWords.length > 0
      ? Math.round((matched / personaWords.length) * 100)
      : 0;

    if (!domainMatched && score < 100) {
      score = Math.min(score, 40);
    }

    if (score > bestScore) {
      bestScore = score;
      bestMatch = persona;
    }
  }

  return { match: bestMatch, score: bestScore };
}

export interface ResearchedPersonaTargets {
  organisationPersonas?: { title: string; description: string; aliases: string[]; products: string[] }[];
  personaImpactMap: PersonaImpactRow[];
  valueStatements: PersonaValueStatement[];
}

export function extractPersonaTargetsFromResearch(
  research: ResearchedPersonaTargets | null,
  useCase: UseCaseItem
): string[] {
  const targets = new Set<string>();
  if (research?.organisationPersonas) {
    const text = [useCase.title, useCase.business_context, useCase.strategic_alignment,
      useCase.vendor_value_proposition, ...(useCase.primary_workflows || [])].join(' ').toLowerCase();
    const supported = research.organisationPersonas.filter(p => {
      const context = [p.description, ...p.products].join(' ').toLowerCase();
      return extractDomainKeywords(context).some(k => extractDomainKeywords(text).includes(k));
    });
    return (supported.length ? supported : research.organisationPersonas)
      .flatMap(p => [p.title, ...(p.aliases || [])]);
  }

  if (research) {
    const ucTextLower = [
      useCase.title,
      useCase.business_context,
      useCase.strategic_alignment,
      useCase.vendor_value_proposition,
      ...(useCase.primary_workflows || []),
    ].join(' ').toLowerCase();

    if (research.personaImpactMap) {
      for (const row of research.personaImpactMap) {
        if (!row.persona) continue;
        const accountabilityLower = (row.strategic_accountability || '').toLowerCase();
        const riskLower = (row.executive_risk || '').toLowerCase();
        const combinedContext = accountabilityLower + ' ' + riskLower;

        const relevanceKeywords = extractDomainKeywords(ucTextLower);
        const contextKeywords = extractDomainKeywords(combinedContext);
        const overlap = relevanceKeywords.filter(k => contextKeywords.includes(k));

        if (overlap.length > 0 || relevanceKeywords.length === 0) {
          const titles = row.persona.split(/\sor\s/i);
          for (const t of titles) {
            targets.add(t.trim());
          }
        }
      }
    }

    if (research.valueStatements) {
      for (const vs of research.valueStatements) {
        if (!vs.persona) continue;
        const stmtLower = (vs.statement_text || '').toLowerCase();

        const relevanceKeywords = extractDomainKeywords(ucTextLower);
        const stmtKeywords = extractDomainKeywords(stmtLower);
        const overlap = relevanceKeywords.filter(k => stmtKeywords.includes(k));

        if (overlap.length > 0 || relevanceKeywords.length === 0) {
          const titles = vs.persona.split(/\sor\s/i);
          for (const t of titles) {
            targets.add(t.trim());
          }
        }
      }
    }
  }

  if (targets.size > 0) return Array.from(targets);

  return extractPersonaTargetsForUseCase(useCase);
}

function extractDomainKeywords(text: string): string[] {
  const keywords: string[] = [];
  const patterns: [RegExp, string][] = [
    [/\b(?:sales|revenue|pipeline|forecast|selling|gtm)\b/i, 'sales'],
    [/\b(?:enablement|coaching|training|onboarding|learning)\b/i, 'enablement'],
    [/\b(?:customer success|retention|renewal|adoption|expansion)\b/i, 'customer success'],
    [/\b(?:marketing|campaign|brand|demand generation)\b/i, 'marketing'],
    [/\b(?:hr|human resources|people|talent|workforce)\b/i, 'people'],
    [/\b(?:legal|contract|counsel)\b/i, 'legal'],
    [/\b(?:security|cybersecurity|infosec|cyber)\b/i, 'security'],
    [/\b(?:engineering|software|development)\b/i, 'engineering'],
    [/\b(?:infrastructure|cloud|network|systems)\b/i, 'infrastructure'],
    [/\b(?:data|analytics|ai|machine learning)\b/i, 'data'],
    [/\b(?:platform)\b/i, 'platform'],
    [/\b(?:sre|site reliability|devops)\b/i, 'sre'],
    [/\b(?:pricing|underwriting|quote|motor)\b/i, 'pricing'],
    [/\b(?:claims|dispatch|rescue|repair)\b/i, 'claims'],
    [/\b(?:digital|app|self-service|servicing)\b/i, 'digital'],
    [/\b(?:home|property|re-?platform)\b/i, 'home'],
    [/\b(?:operations|supply chain)\b/i, 'operations'],
    [/\b(?:risk|compliance|governance)\b/i, 'risk'],
    [/\b(?:finance|cost|budget)\b/i, 'finance'],
    [/\b(?:product|ux)\b/i, 'product'],
  ];
  for (const [pattern, keyword] of patterns) {
    if (pattern.test(text)) keywords.push(keyword);
  }
  return keywords;
}

export function extractPersonaTargetsForUseCase(useCase: UseCaseItem): string[] {
  const targets = new Set<string>();

  const textBlocks = [
    useCase.vendor_value_proposition || '',
    useCase.business_context || '',
    useCase.strategic_alignment || '',
    useCase.business_impact || '',
    useCase.sales_hook || '',
    ...(useCase.sales_conversation_guidance?.key_talking_points || []),
    ...(useCase.sales_conversation_guidance?.discovery_questions || []),
    useCase.sales_conversation_guidance?.opening_hook || '',
    useCase.sales_conversation_guidance?.call_to_action || '',
  ];

  const combinedText = textBlocks.join(' ');

  const patterns = [
    /\bVP\s+(?:of\s+)?[\w&]+(?:\s+[\w&]+)?/gi,
    /\bHead\s+of\s+[\w&]+(?:\s+[\w&]+)?/gi,
    /\bDirector\s+(?:of\s+)?[\w&]+(?:\s+[\w&]+)?/gi,
    /\bSenior\s+Director\s+(?:of\s+)?[\w&]+(?:\s+[\w&]+)?/gi,
    /\bSr\.?\s+Director\s+(?:of\s+)?[\w&]+(?:\s+[\w&]+)?/gi,
    /\bManager\s+(?:of\s+)?[\w&]+(?:\s+[\w&]+)?/gi,
    /\bSVP\s+(?:of\s+)?[\w&]+(?:\s+[\w&]+)?/gi,
    /\bEVP\s+(?:of\s+)?[\w&]+(?:\s+[\w&]+)?/gi,
    /\b(?:CIO|CTO|CISO|CFO|COO|CEO|CRO|CPO|CMO)\b/gi,
    /\b(?:Chief\s+(?:Information|Technology|Security|Financial|Operating|Revenue|Product|Marketing)\s+Officer)\b/gi,
    /\bPrincipal\s+[\w&]+(?:\s+[\w&]+)?/gi,
    /\bStaff\s+[\w&]+(?:\s+[\w&]+)?/gi,
    /\bSenior\s+(?:Engineer|Architect|Analyst|Developer|Manager|Lead)\b/gi,
    /\bEnterprise\s+Architect\b/gi,
    /\bSolutions?\s+Architect\b/gi,
    /\bPlatform\s+(?:Lead|Manager|Director|Engineer)\b/gi,
    /\bSecurity\s+(?:Lead|Manager|Director|Engineer|Architect)\b/gi,
    /\bCloud\s+(?:Lead|Manager|Director|Engineer|Architect)\b/gi,
    /\bInfrastructure\s+(?:Lead|Manager|Director|Engineer)\b/gi,
    /\bEngineering\s+(?:Lead|Manager|Director)\b/gi,
  ];

  for (const pattern of patterns) {
    const matches = combinedText.match(pattern);
    if (matches) {
      for (const m of matches) {
        const cleaned = m.trim().replace(/\s+/g, ' ');
        if (cleaned.length > 2) targets.add(cleaned);
      }
    }
  }

  if (targets.size === 0) {
    const deptKeywords = extractDepartmentKeywordsLegacy(combinedText);
    for (const dept of deptKeywords) {
      targets.add(`VP ${dept}`);
      targets.add(`Head of ${dept}`);
      targets.add(`Director of ${dept}`);
    }
  }

  if (targets.size === 0) {
    return [];
  }

  return Array.from(targets);
}

function extractDepartmentKeywordsLegacy(text: string): string[] {
  const deptPatterns = [
    { pattern: /\b(?:sales|revenue|pipeline|forecast|gtm)\b/i, dept: 'Sales' },
    { pattern: /\b(?:enablement|coaching|training|learning)\b/i, dept: 'Enablement' },
    { pattern: /\b(?:customer success|renewal|retention|adoption)\b/i, dept: 'Customer Success' },
    { pattern: /\b(?:marketing|campaign|brand)\b/i, dept: 'Marketing' },
    { pattern: /\b(?:human resources|talent|workforce)\b/i, dept: 'Human Resources' },
    { pattern: /\b(?:security|cybersecurity|infosec)\b/i, dept: 'Security' },
    { pattern: /\b(?:engineering|software|development)\b/i, dept: 'Engineering' },
    { pattern: /\b(?:infrastructure|cloud|network|systems)\b/i, dept: 'Infrastructure' },
    { pattern: /\b(?:data|analytics|ai|machine learning)\b/i, dept: 'Data' },
    { pattern: /\b(?:product|ux)\b/i, dept: 'Product' },
    { pattern: /\b(?:operations)\b/i, dept: 'Operations' },
    { pattern: /\b(?:platform)\b/i, dept: 'Platform' },
    { pattern: /\b(?:it|information technology)\b/i, dept: 'IT' },
    { pattern: /\b(?:risk|compliance|governance)\b/i, dept: 'Risk & Compliance' },
    { pattern: /\b(?:finance|financial)\b/i, dept: 'Finance' },
  ];

  const found: string[] = [];
  for (const { pattern, dept } of deptPatterns) {
    if (pattern.test(text)) found.push(dept);
  }
  return found;
}

export function computeRelevanceScore(contact: Contact): number {
  const personaWeight = 0.4;
  const seniorityWeight = 0.3;
  const alignmentWeight = 0.3;

  const personaScore = contact.persona_match_score || 0;

  const seniorityMap: Record<string, number> = {
    executive: 100,
    executive_leadership: 85,
    senior_leadership: 75,
    department_leader: 60,
    team_manager: 40,
    individual_contributor: 20,
  };
  const normSeniority = normaliseSeniority(contact.seniority, contact.role).seniority;
  const seniorityScore = seniorityMap[normSeniority] || 20;

  const alignmentScore = contact.use_case_relevance_score || 0;

  return Math.round(
    personaScore * personaWeight +
    seniorityScore * seniorityWeight +
    alignmentScore * alignmentWeight
  );
}

export function computeUseCaseRelevanceForContact(
  contact: Contact,
  useCase: UseCaseItem,
  personaTargets: string[]
): {
  persona_match: string;
  persona_match_score: number;
  use_case_relevance_score: number;
  outbound_priority: OutboundPriority;
  priority_rationale: string;
} {
  const { match, score: personaScore } = computePersonaMatch(contact.role, personaTargets);

  const seniorityMap: Record<string, number> = {
    executive: 100,
    executive_leadership: 85,
    senior_leadership: 75,
    department_leader: 60,
    team_manager: 40,
    individual_contributor: 20,
  };
  const normSeniority = normaliseSeniority(contact.seniority, contact.role).seniority;
  const seniorityScore = seniorityMap[normSeniority] || 20;

  const departmentRelevance = computeDepartmentRelevance(contact, useCase);

  const useCaseRelevance = Math.round(
    personaScore * 0.35 +
    seniorityScore * 0.25 +
    departmentRelevance * 0.25 +
    (contact.buying_committee_role ? 15 : 0)
  );

  const { priority, rationale } = assignOutboundPriority(
    contact,
    personaScore,
    useCaseRelevance,
    departmentRelevance,
    useCase
  );

  return {
    persona_match: match,
    persona_match_score: personaScore,
    use_case_relevance_score: Math.min(useCaseRelevance, 100),
    outbound_priority: priority,
    priority_rationale: rationale,
  };
}

function computeDepartmentRelevance(contact: Contact, useCase: UseCaseItem): number {
  const ucText = [
    useCase.title,
    useCase.vendor_value_proposition,
    useCase.business_context,
    useCase.strategic_alignment,
    ...(useCase.primary_workflows || []),
    ...(useCase.primary_kpi_categories || []),
  ].join(' ').toLowerCase();

  const dept = (contact.department || '').toLowerCase();
  const role = (contact.role || '').toLowerCase();
  const combinedContact = `${dept} ${role}`;

  const domainPairs: [string[], string[]][] = [
    [['sales', 'revenue', 'cro', 'business development', 'revops'], ['sales', 'revenue', 'pipeline', 'forecast', 'selling', 'gtm', 'conversion', 'quota', 'coaching']],
    [['enablement', 'training', 'learning', 'l&d'], ['enablement', 'training', 'learning', 'onboarding', 'coaching', 'skills', 'methodology', 'ramp']],
    [['customer success', 'cs', 'account management'], ['customer success', 'adoption', 'renewal', 'retention', 'expansion', 'churn', 'customer']],
    [['marketing', 'cmo', 'brand'], ['marketing', 'brand', 'campaign', 'demand', 'lead', 'acquisition']],
    [['hr', 'human resources', 'people', 'talent'], ['workforce', 'people', 'talent', 'learning', 'skills', 'training', 'employee', 'onboarding']],
    [['legal', 'counsel'], ['legal', 'contract', 'compliance', 'negotiation', 'risk']],
    [['security', 'cyber', 'infosec', 'ciso', 'defence', 'defense', 'assurance'], ['security', 'threat', 'vulnerability', 'compliance', 'risk', 'zero trust', 'soc', 'incident', 'cyber', 'observability', 'telemetry', 'monitoring', 'detection', 'log', 'trace', 'alert']],
    [['engineering', 'software', 'developer', 'sre', 'devops'], ['engineering', 'developer', 'code', 'pipeline', 'ci/cd', 'deployment', 'software', 'devops', 'sre', 'release', 'reliability', 'observability', 'telemetry', 'monitoring', 'log', 'trace']],
    [['infrastructure', 'cloud', 'network', 'systems', 'platform'], ['infrastructure', 'cloud', 'network', 'kubernetes', 'container', 'platform', 'compute', 'server', 'hosting', 'observability', 'monitoring', 'telemetry', 'log', 'trace']],
    [['data', 'analytics', 'ai', 'ml', 'machine learning'], ['data', 'analytics', 'ai', 'machine learning', 'insight', 'dashboard', 'warehouse', 'lake', 'pipeline', 'pricing', 'model']],
    [['product', 'ux', 'design'], ['product', 'user experience', 'feature', 'roadmap', 'design', 'journey', 'digital', 'app']],
    [['it', 'information technology', 'technology', 'architecture'], ['it ', 'information technology', 'helpdesk', 'service desk', 'itsm', 'architecture', 'technology', 'observability', 'monitoring', 'telemetry', 'integration']],
    [['operations', 'supply chain'], ['operations', 'supply chain', 'logistics', 'workflow', 'process', 'efficiency', 'claims', 'dispatch', 'rescue']],
    [['finance', 'accounting'], ['finance', 'cost', 'budget', 'roi', 'revenue', 'savings', 'spend', 'procurement', 'synergy']],
    [['risk', 'compliance', 'governance'], ['risk', 'compliance', 'governance', 'audit', 'regulatory', 'policy', 'security']],
  ];

  let bestScore = 0;
  for (const [contactKeywords, ucKeywords] of domainPairs) {
    const contactMatch = contactKeywords.some(kw => combinedContact.includes(kw));
    if (!contactMatch) continue;
    const ucHits = ucKeywords.filter(kw => ucText.includes(kw)).length;
    const ucScore = Math.min(Math.round((ucHits / ucKeywords.length) * 100), 100);
    if (ucScore > bestScore) bestScore = ucScore;
  }

  return bestScore;
}

function assignOutboundPriority(
  contact: Contact,
  personaScore: number,
  relevanceScore: number,
  deptRelevance: number,
  _useCase: UseCaseItem
): { priority: OutboundPriority; rationale: string } {
  const normSeniority = normaliseSeniority(contact.seniority, contact.role).seniority;
  const isExecutive = normSeniority === 'executive' || normSeniority === 'executive_leadership';
  const isSeniorLeader = normSeniority === 'senior_leadership';
  const isDeptLeader = normSeniority === 'department_leader';
  const isEconomicBuyer = contact.buying_committee_role === 'economic_buyer';
  const isTechnicalBuyer = contact.buying_committee_role === 'technical_buyer';
  const isChampion = contact.buying_committee_role === 'champion';

  if (personaScore >= 60 && (isExecutive || isSeniorLeader || isDeptLeader) && deptRelevance >= 30) {
    return {
      priority: 'target_persona',
      rationale: `Strong persona match (${personaScore}%) with ${SENIORITY_LABELS[normSeniority] || normSeniority} presence in a relevant department (${deptRelevance}% alignment). This person will feel the strategic pain and can champion the value.`
    };
  }

  if (personaScore >= 80 && deptRelevance >= 40) {
    return {
      priority: 'target_persona',
      rationale: `Very high persona match (${personaScore}%) in a directly relevant department. Key individual for this use case.`
    };
  }

  if ((isExecutive || isSeniorLeader) && (isEconomicBuyer || isChampion) && deptRelevance >= 20) {
    return {
      priority: 'decision_maker',
      rationale: `Senior leader (${SENIORITY_LABELS[normSeniority] || normSeniority}) with ${BUYING_ROLE_LABELS[contact.buying_committee_role] || 'buying'} authority. Will evaluate fit and make or influence the purchase decision.`
    };
  }

  if (isDeptLeader && deptRelevance >= 30 && relevanceScore >= 40) {
    return {
      priority: 'decision_maker',
      rationale: `Department leader in a relevant area with good alignment (${relevanceScore}%). Likely involved in evaluation and budget decisions.`
    };
  }

  if (isDeptLeader && isChampion && deptRelevance >= 20) {
    return {
      priority: 'decision_maker',
      rationale: `Department leader with Champion role in a relevant domain. Positioned to influence and potentially own the evaluation.`
    };
  }

  if (isTechnicalBuyer && deptRelevance >= 25) {
    return {
      priority: 'influencer',
      rationale: `Technical buyer in a related domain. Will validate technical fit and influence the decision with hands-on evaluation.`
    };
  }

  if (contact.buying_committee_role === 'influencer' && deptRelevance >= 20) {
    return {
      priority: 'influencer',
      rationale: `Influencer role in a relevant department. Can provide internal advocacy and functional-level intelligence.`
    };
  }

  if (personaScore >= 30 && deptRelevance >= 15) {
    return {
      priority: 'influencer',
      rationale: `Partial persona match (${personaScore}%) with some department alignment. Can provide context and validate assumptions.`
    };
  }

  if ((contact.buying_committee_role === 'validator' || isTechnicalBuyer) && deptRelevance >= 10) {
    return {
      priority: 'validator',
      rationale: `Validator/technical role. Good for validating intelligence, understanding functional pain, and gathering early insights.`
    };
  }

  if (deptRelevance >= 20 && relevanceScore >= 25) {
    return {
      priority: 'validator',
      rationale: `Some department alignment. Can help validate assumptions and provide ground-level intelligence.`
    };
  }

  return {
    priority: 'low_relevance',
    rationale: `Low alignment to this specific use case. May be relevant for other use cases or general relationship building.`
  };
}

export type ContactHighlight = 'primary' | 'decision_maker' | 'validator' | 'low';

export function getContactHighlight(contact: Contact): ContactHighlight {
  const relevance = computeRelevanceScore(contact);
  if (relevance >= 70) return 'primary';
  if (relevance >= 50) return 'decision_maker';
  if (relevance >= 30) return 'validator';
  return 'low';
}

export function getHighlightFromPriority(priority: OutboundPriority): ContactHighlight {
  switch (priority) {
    case 'target_persona': return 'primary';
    case 'decision_maker': return 'decision_maker';
    case 'influencer':
    case 'validator': return 'validator';
    default: return 'low';
  }
}

export function getEffectivePriority(score: { outbound_priority: OutboundPriority; priority_override?: OutboundPriority | null }): OutboundPriority {
  return score.priority_override || score.outbound_priority;
}

export const HIGHLIGHT_COLORS: Record<ContactHighlight, { bg: string; border: string; text: string; dot: string }> = {
  primary: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700', dot: 'bg-red-500' },
  decision_maker: { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-700', dot: 'bg-orange-500' },
  validator: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', dot: 'bg-amber-500' },
  low: { bg: 'bg-app-surface-raised', border: 'border-app-line', text: 'text-app-muted', dot: 'bg-app-muted' },
};

export const HIGHLIGHT_LABELS: Record<ContactHighlight, string> = {
  primary: 'Target Persona',
  decision_maker: 'Decision Maker',
  validator: 'Validator / Influencer',
  low: 'Low Relevance',
};

export const PRIORITY_LABELS: Record<OutboundPriority, string> = {
  target_persona: 'Target Persona',
  decision_maker: 'Decision Maker',
  influencer: 'Influencer',
  validator: 'Validator',
  low_relevance: 'Low Relevance',
};

export const PRIORITY_ORDER: Record<OutboundPriority, number> = {
  target_persona: 0,
  decision_maker: 1,
  influencer: 2,
  validator: 3,
  low_relevance: 4,
};

export const SENIORITY_LABELS: Record<string, string> = {
  executive: 'Executive (C-Suite)',
  executive_leadership: 'Executive Leadership (SVP/EVP)',
  senior_leadership: 'Senior Leadership (VP)',
  department_leader: 'Department Leader (Director/Head)',
  team_manager: 'Team Manager',
  individual_contributor: 'Individual Contributor',
};

export const BUYING_ROLE_LABELS: Record<string, string> = {
  economic_buyer: 'Economic Buyer',
  technical_buyer: 'Technical Buyer',
  champion: 'Champion',
  influencer: 'Influencer',
  validator: 'Validator',
};

export interface MissingPersona {
  persona: string;
  bestContactMatch: string;
  bestScore: number;
}

export function detectMissingPersonas(
  personaTargets: string[],
  contacts: Contact[],
  scoreMap: Map<string, { persona_match_score: number; persona_match: string }>
): MissingPersona[] {
  const missing: MissingPersona[] = [];
  const threshold = 60;

  for (const persona of personaTargets) {
    let bestScore = 0;
    let bestContact = '';

    for (const c of contacts) {
      const s = scoreMap.get(c.id);
      if (!s) continue;
      const { score } = computePersonaMatch(c.role, [persona]);
      if (score > bestScore) {
        bestScore = score;
        bestContact = `${c.first_name} ${c.last_name} (${c.role})`;
      }
    }

    if (bestScore < threshold) {
      missing.push({
        persona,
        bestContactMatch: bestContact,
        bestScore,
      });
    }
  }

  return missing;
}

export function extractDepartmentKeywordsFromUseCases(useCases: UseCaseItem[]): string[] {
  const deptSet = new Set<string>();
  const deptPatterns: [RegExp, string][] = [
    [/\b(?:security|cybersecurity|infosec|cyber)\b/i, 'Security'],
    [/\b(?:engineering|software|development)\b/i, 'Engineering'],
    [/\b(?:infrastructure|cloud|network|systems)\b/i, 'Infrastructure'],
    [/\b(?:data|analytics|ai|machine learning)\b/i, 'Data & Analytics'],
    [/\b(?:product|ux|design)\b/i, 'Product'],
    [/\b(?:operations|supply chain)\b/i, 'Operations'],
    [/\b(?:platform)\b/i, 'Platform'],
    [/\b(?:it|information technology)\b/i, 'IT'],
    [/\b(?:risk|compliance|governance)\b/i, 'Risk & Compliance'],
    [/\b(?:finance|financial)\b/i, 'Finance'],
    [/\b(?:devops|sre|site reliability)\b/i, 'DevOps'],
  ];

  for (const uc of useCases) {
    const text = [
      uc.title,
      uc.vendor_value_proposition,
      uc.business_context,
      uc.strategic_alignment,
      ...(uc.primary_workflows || []),
      ...(uc.primary_kpi_categories || []),
    ].join(' ');

    for (const [pattern, dept] of deptPatterns) {
      if (pattern.test(text)) deptSet.add(dept);
    }
  }

  return Array.from(deptSet);
}

export function buildInferredExecutiveContacts(
  account: { id: string; company_name: string; key_executives: { name: string; title: string }[] },
  existingContacts: Contact[]
): Contact[] {
  const executives = Array.isArray(account.key_executives) ? account.key_executives : [];
  if (executives.length === 0) return [];

  const existingNames = new Set(
    existingContacts.map(c => `${c.first_name} ${c.last_name}`.toLowerCase().trim())
  );

  const inferred: Contact[] = [];

  for (const exec of executives) {
    const nameParts = (exec.name || '').trim().split(/\s+/);
    if (nameParts.length === 0 || !nameParts[0]) continue;

    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ') || '';
    const fullName = `${firstName} ${lastName}`.toLowerCase().trim();

    if (existingNames.has(fullName)) continue;

    const { seniority, level } = inferSeniority(exec.title || '');
    const department = inferDepartment(exec.title || '');
    const buyingRole = inferBuyingRole(exec.title || '');

    inferred.push({
      id: `inferred-${crypto.randomUUID()}`,
      account_id: account.id,
      first_name: firstName,
      last_name: lastName,
      role: exec.title || '',
      organisation: account.company_name,
      email: '',
      phone: '',
      department,
      department_id: null,
      seniority,
      linkedin_url: '',
      location: '',
      buying_committee_role: buyingRole,
      persona_match: '',
      persona_match_score: 0,
      use_case_relevance_score: 0,
      org_level: level,
      reports_to_id: null,
      org_placement_confidence: 40,
      notes: '',
      source: 'inferred_executive',
      is_inferred: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  }

  return inferred;
}

export function groupContactsByDepartment(
  contacts: Contact[],
  departments: AccountDepartment[]
): Map<string, Contact[]> {
  const groups = new Map<string, Contact[]>();

  const deptNameMap = new Map<string, AccountDepartment>();
  for (const d of departments) {
    deptNameMap.set(d.name.toLowerCase(), d);
  }

  for (const contact of contacts) {
    const dept = contact.department || 'Unassigned';
    if (!groups.has(dept)) groups.set(dept, []);
    groups.get(dept)!.push(contact);
  }

  const ordered = new Map<string, Contact[]>();
  const sortedDepts = [...departments].sort((a, b) => {
    if (a.is_relevant && !b.is_relevant) return -1;
    if (!a.is_relevant && b.is_relevant) return 1;
    return a.sort_order - b.sort_order;
  });

  for (const dept of sortedDepts) {
    const key = dept.name;
    const matched = groups.get(key) || [];
    if (matched.length > 0) {
      ordered.set(key, matched);
      groups.delete(key);
    }
  }

  for (const [key, contacts] of groups) {
    if (key !== 'Unassigned') {
      ordered.set(key, contacts);
    }
  }

  const unassigned = groups.get('Unassigned');
  if (unassigned && unassigned.length > 0) {
    ordered.set('Unassigned', unassigned);
  }

  return ordered;
}

export function wouldCreateCycle(
  contacts: Contact[],
  contactId: string,
  newManagerId: string | null
): boolean {
  if (!newManagerId) return false;
  if (contactId === newManagerId) return true;

  const reportsMap = new Map<string, string | null>();
  for (const c of contacts) {
    reportsMap.set(c.id, c.reports_to_id);
  }
  reportsMap.set(contactId, newManagerId);

  let current: string | null = newManagerId;
  const visited = new Set<string>();
  visited.add(contactId);

  while (current) {
    if (visited.has(current)) return true;
    visited.add(current);
    current = reportsMap.get(current) ?? null;
  }

  return false;
}
